<template>
  <div class="pa-2">
    <div>
        <v-card
          max-width="900"
          color="#F8F8F8"
          class="pa-2 px-6 mx-auto report-card my-4"
        >
          <v-card-text class="d-flex align-center mb-2">
            <v-icon class="mr-2">mdi-magnify</v-icon>
            <span class="subtitle">{{ $t('Filtros de Busca') }}</span>
          </v-card-text>

          <v-row class="ma-0" dense>
            <v-col cols="12" sm="4">
              <label class="label ml-1">{{ $t('Instituição') }}</label>
              <v-autocomplete
                v-model="filter.institutionIds"
                :loading="institutionLoading"
                :items="institutions"
                item-text="name"
                item-value="id"
                hide-details
                outlined
                class="primary-input"
                multiple
                chips
                small-chips
                deletable-chips
              >
                <template v-slot:selection="{ attrs, item, select, selected, index }">
                  <v-chip
                    v-if="index === 0"
                    v-bind="attrs"
                    :input-value="selected"
                    close
                    color="#BBCF20"
                    text-color="#fff"
                    small
                    @click="select"
                    @click:close="removeInstitution(item)"
                  >
                    {{ item.name }}
                  </v-chip>
                  <span
                    v-if="index === 1"
                    class="grey--text text-caption"
                    style="font-size: 0.8rem"
                  >
                    ( +{{ filter.institutionIds.length - 1 }})
                  </span>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12" sm="4">
              <label class="label ml-1">{{ $t('Unidade') }}</label>
              <v-autocomplete
                v-model="filter.unidadeIds"
                :loading="unidadeLoading"
                :items="unidades"
                item-text="name"
                item-value="id"
                hide-details
                outlined
                class="primary-input"
                multiple
                chips
                small-chips
                deletable-chips
              >
                <template v-slot:selection="{ attrs, item, select, selected, index }">
                  <v-chip
                    v-if="index === 0"
                    v-bind="attrs"
                    :input-value="selected"
                    close
                    color="#BBCF20"
                    text-color="#fff"
                    small
                    @click="select"
                    @click:close="removeUnidade(item)"
                  >
                    {{ item.name }}
                  </v-chip>
                  <span
                    v-if="index === 1"
                    class="grey--text text-caption"
                    style="font-size: 0.8rem"
                  >
                    ( +{{ filter.unidadeIds.length - 1 }})
                  </span>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12" sm="4">
              <label class="label ml-1">{{ $t('Setor') }}</label>
              <v-autocomplete
                v-model="filter.departmentIds"
                :loading="departmentLoading"
                :items="departments"
                :item-text="(item) => item.unidade ? `${item.name} / ${item.unidade.name}` : item.name"
                item-value="id"
                hide-details
                outlined
                class="primary-input"
                multiple
                chips
                small-chips
                deletable-chips
              >
                <template v-slot:selection="{ attrs, item, select, selected, index }">
                  <v-chip
                    v-if="index === 0"
                    v-bind="attrs"
                    :input-value="selected"
                    close
                    color="#BBCF20"
                    text-color="#fff"
                    small
                    @click="select"
                    @click:close="removeDepartment(item)"
                  >
                    {{ item.unidade ? `${item.name} / ${item.unidade.name}` : item.name }}
                  </v-chip>
                  <span
                    v-if="index === 1"
                    class="grey--text text-caption"
                    style="font-size: 0.8rem"
                  >
                    ( +{{ filter.departmentIds.length - 1 }})
                  </span>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12" class="d-flex align-center justify-center mb-2 flex-wrap">
              <v-menu
                ref="startMenu"
                :close-on-content-click="false"
                :return-value.sync="filter.startDate"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    depressed
                    class="primary-button"
                    style="text-transform: none"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ formatMonth(filter.startDate) }}
                  </v-btn>
                </template>
                <v-date-picker
                  v-model="filter.startDate"
                  type="month"
                  locale="pt-br"
                  no-title
                  scrollable
                  @change="$refs.startMenu.save(filter.startDate)"
                />
              </v-menu>

              <span class="mx-2 subtitle">{{ $t('até') }}</span>

              <v-menu
                ref="endMenu"
                :close-on-content-click="false"
                :return-value.sync="filter.endDate"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    depressed
                    class="primary-button"
                    style="text-transform: none"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ formatMonth(filter.endDate) }}
                  </v-btn>
                </template>
                <v-date-picker
                  v-model="filter.endDate"
                  type="month"
                  locale="pt-br"
                  no-title
                  scrollable
                  @change="$refs.endMenu.save(filter.endDate)"
                />
              </v-menu>
            </v-col>

            <v-col class="d-flex align-center justify-center mb-4 flex-wrap" cols="12">
              <v-btn depressed class="secondary-button mr-1" @click="filterTable">
                <span>{{ $t('Filtrar') }}</span>
                <v-icon class="ml-2">mdi-magnify</v-icon>
              </v-btn>
              <v-btn depressed class="clear-button ml-1" @click="reset">
                <span>{{ $t('Limpar') }}</span>
                <v-icon class="ml-2">mdi-cached</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
    </div>

    <div class="wrapper desktop">
      <v-data-table
        :loading="loading"
        :headers="headers"
        :items="items"
        class="main-table"
        hide-default-footer
        hide-default-header
        :items-per-page="pageSize"
        :headerProps="headerProps"
        :item-class="isInactive"
        sort-by="totalCost"
        :sort-desc="true"
      >
        <template #header="{ props, on }">
          <thead>
            <tr>
              <th
                class="text-center"
                v-for="header in props.headers"
                :key="header.value"
                :style="{ width: header.width }"
              >
                <div>
                  <v-hover v-slot="{ hover }">
                    <div>
                      <span>{{ header.text }}</span>
                      <v-icon
                        v-if="
                          hover &&
                          header.value != 'data-table-select' &&
                          header.value != 'actions'
                        "
                        color="white"
                        class="mx-2"
                        @click="on.sort(header.value)"
                        >{{
                          props.options.sortBy.includes(header.value)
                            ? 'mdi-chevron-down'
                            : 'mdi-chevron-up'
                        }}</v-icon
                      >
                    </div>
                  </v-hover>
                </div>
              </th>
            </tr>
          </thead>
        </template>
        <template #[`item.location`]="{ item }">
          {{ item.institution }} - {{ item.unidade }} - {{ item.department }}
        </template>
        <template #[`item.partCost`]="{ item }">
          {{ formatCurrency(item.partCost) }}
        </template>
        <template #[`item.externalServiceCost`]="{ item }">
          {{ formatCurrency(item.externalServiceCost) }}
        </template>
        <template #[`item.contractCost`]="{ item }">
          {{ formatCurrency(item.contractCost) }}
        </template>
        <template #[`item.internalLaborCost`]="{ item }">
          {{ formatCurrency(item.internalLaborCost) }}
        </template>
        <template #[`item.totalCost`]="{ item }">
          {{ formatCurrency(item.totalCost) }}
        </template>
      </v-data-table>
      <div class="pagination">
        <div class="item-pagination block-one">
          <v-menu bottom offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                class="btn"
                depressed
                color="#C0C0C0"
                small
              >
                qtd: {{ pageSize }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="(size, i) in sizes"
                :key="i"
                @click="pageSize = size"
              >
                <v-list-item-title>{{ size }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <div class="registros" v-if="totalItensOnPage">
            <span> 1 - {{ totalItensOnPage }} de {{ totalItems }} </span> <br />
          </div>
        </div>

        <div class="item-pagination block-two">
          <v-pagination
            class="pagination-item"
            v-model="page"
            :length="totalPages"
            :total-visible="7"
          ></v-pagination>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import ApiService from '../../../../services/ApiService'
import moment from 'moment'

export default {
  data() {
    const lastMonth = moment().subtract(1, 'months').format('YYYY-MM')
    return {
      apiService: new ApiService(),
      institutionId: null,
      userLogged: {},
      institutions: [],
      unidades: [],
      departments: [],
      institutionLoading: false,
      unidadeLoading: false,
      departmentLoading: false,
      loading: false,
      items: [],
      page: 1,
      totalPages: 1,
      pageSize: 10,
      sizes: [10, 30, 90, 180],
      totalItems: 0,
      totalItensOnPage: 0,
      headers: [
        { text: 'Local', value: 'location', align: 'center', width: '35%' },
        { text: 'Custo Peça', value: 'partCost', align: 'center', width: '13%' },
        { text: 'Custo Serviço Externo', value: 'externalServiceCost', align: 'center', width: '13%' },
        { text: 'Custo Contrato', value: 'contractCost', align: 'center', width: '13%' },
        { text: 'Custo MdO Interna', value: 'internalLaborCost', align: 'center', width: '13%' },
        { text: 'Custo Total', value: 'totalCost', align: 'center', width: '13%' },
      ],
      headerProps: {
        sortByText: 'Ordenar por',
      },
      filter: {
        institutionIds: [],
        unidadeIds: [],
        departmentIds: [],
        startDate: lastMonth,
        endDate: lastMonth,
        page: 1,
        pageSize: 10,
      },
    }
  },
  created() {
    this.userLogged = JSON.parse(localStorage.getItem('user'))
    this.institutionId = JSON.parse(localStorage.getItem('institution')).id

    this.filter.institutionIds = [this.institutionId]
    this.updateTable()
    this.getInstitutions()
  },
  methods: {
    async getInstitutions() {
      this.institutionLoading = true

      await this.apiService
        .get('institution/listuser')
        .then((response) => {
          this.institutions = response.content
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar instituições')
        })
        .finally(() => {
          this.institutionLoading = false
        })
    },

    async getUnidades() {
      this.unidadeLoading = true

      if (!this.filter.institutionIds || this.filter.institutionIds.length === 0) {
        this.unidades = []
        this.unidadeLoading = false
        return
      }

      await this.apiService
        .post('bigdata/unidade', this.filter.institutionIds)
        .then((response) => {
          this.unidades = response.content
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar unidades')
        })
        .finally(() => {
          this.unidadeLoading = false
        })
    },

    async getDepartments() {
      this.departmentLoading = true

      if (!this.filter.institutionIds || this.filter.institutionIds.length === 0) {
        this.departments = []
        this.departmentLoading = false
        return
      }

      await this.apiService
        .post('bigdata/department', this.filter.institutionIds)
        .then((response) => {
          this.departments = response.content
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar departamentos')
        })
        .finally(() => {
          this.departmentLoading = false
        })
    },

    removeInstitution(item) {
      const index = this.filter.institutionIds.indexOf(item.id)
      if (index >= 0) {
        this.filter.institutionIds.splice(index, 1)
        this.filter.institutionIds = [...this.filter.institutionIds]
      }
    },

    removeUnidade(item) {
      const index = this.filter.unidadeIds.indexOf(item.id)
      if (index >= 0) {
        this.filter.unidadeIds.splice(index, 1)
        this.filter.unidadeIds = [...this.filter.unidadeIds]
      }
    },

    removeDepartment(item) {
      const index = this.filter.departmentIds.indexOf(item.id)
      if (index >= 0) {
        this.filter.departmentIds.splice(index, 1)
        this.filter.departmentIds = [...this.filter.departmentIds]
      }
    },

    filterTable() {
      this.page = 1
      this.filter.page = this.page
      this.filter.pageSize = this.pageSize
      this.updateTable()
    },

    async updateTable() {
      this.loading = true

      await this.apiService
        .post('bigdata/getCostsReport', this.filter)
        .then((response) => {
          this.items = response.content.items
          this.totalPages = response.content.pager.totalPages
          this.totalItems = response.content.pager.totalItems
          this.totalItensOnPage = this.items.length
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar dados')
        })
        .finally(() => {
          this.loading = false
        })
    },

    reset() {
      const lastMonth = moment().subtract(1, 'months').format('YYYY-MM')
      this.filter = {
        institutionIds: [],
        unidadeIds: [],
        departmentIds: [],
        startDate: lastMonth,
        endDate: lastMonth,
        page: 1,
        pageSize: this.pageSize,
      }

      this.unidades = []
      this.departments = []
      this.items = []
      this.totalPages = 1
      this.totalItems = 0
      this.totalItensOnPage = 0
    },

    isInactive() {
      return ''
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(value)
    },

    formatMonth(date) {
      return moment(date).locale(this.$i18n.locale).format('MMMM/YYYY')
    },
  },
  watch: {
    'filter.institutionIds'(newVal) {
      if (newVal && newVal.length > 0) {
        this.filter.unidadeIds = []
        this.filter.departmentIds = []
        Promise.all([this.getUnidades(), this.getDepartments()])
      } else {
        this.unidades = []
        this.departments = []
        this.filter.unidadeIds = []
        this.filter.departmentIds = []
      }
    },

    pageSize() {
      this.filter.pageSize = this.pageSize
      this.filter.page = this.page
      this.updateTable()
    },

    page() {
      this.filter.page = this.page
      this.updateTable()
    },
  },
}
</script>
